import { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  ChevronDown,
  Copy,
  Loader2,
  Play,
  Plus,
  RefreshCw,
  RotateCw,
  Square,
  Trash2,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/PageHeader";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  CreateCloudComputerDialog,
  DeleteCloudComputerDialog,
  ResizeCloudComputerDialog,
} from "@/components/cloud-computers/CloudComputerDialogs";
import {
  CLOUD_COMPUTERS_KEY,
  useCloudComputerAction,
  useCloudComputers,
  type CloudComputer,
  type CloudComputerAction,
  type CloudComputerProvider,
  type CloudComputerState,
} from "@/hooks/useCloudComputers";

/** Which row columns fit, decided by the list's own width (the sidebar makes viewport breakpoints unreliable). */
type RowColumns = {
  inline: boolean;
  size: boolean;
  details: boolean;
  privateIp: boolean;
};

function columnsForWidth(width: number): RowColumns {
  return {
    inline: width >= 560,
    size: width >= 680,
    details: width >= 880,
    privateIp: width >= 1060,
  };
}

function useElementWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

type PendingAction = {
  computer: CloudComputer;
  action: CloudComputerAction;
} | null;

const ACTION_CONFIRMATIONS: Record<
  CloudComputerAction,
  { label: string; description: string }
> = {
  start: {
    label: "Start",
    description: "The computer is billed while it is running.",
  },
  stop: {
    label: "Stop",
    description: "Anything running on this computer will be interrupted.",
  },
  restart: {
    label: "Restart",
    description: "Anything running on this computer will be interrupted.",
  },
};

export default function CloudComputersPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, isFetching } = useCloudComputers();
  const computerAction = useCloudComputerAction();
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [createProvider, setCreateProvider] =
    useState<CloudComputerProvider | null>(null);
  const [deleteComputer, setDeleteComputer] = useState<CloudComputer | null>(
    null,
  );
  const [resizeComputer, setResizeComputer] = useState<CloudComputer | null>(
    null,
  );
  const confirmation = pendingAction
    ? ACTION_CONFIRMATIONS[pendingAction.action]
    : null;

  const confirmPendingAction = () => {
    if (!pendingAction) return;
    computerAction.mutate(pendingAction, {
      onSuccess: () => setPendingAction(null),
    });
  };

  return (
    <div className="space-y-6" data-testid="cloud-computers-page">
      <PageHeader title="Cloud Computers">
        <Button
          variant="outline"
          disabled={isFetching}
          onClick={() =>
            queryClient.invalidateQueries({ queryKey: CLOUD_COMPUTERS_KEY })
          }
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </PageHeader>

      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Cloud computers unavailable</AlertTitle>
          <AlertDescription>
            {error instanceof Error
              ? error.message
              : "Failed to load cloud computers"}
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="text-sm text-muted-foreground">
          Loading cloud computers...
        </div>
      ) : (
        data?.providers.map((provider) => (
          <ProviderSection
            key={provider.id}
            provider={provider}
            isBusy={(computer) =>
              computerAction.isPending &&
              computerAction.variables?.computer.provider ===
                computer.provider &&
              computerAction.variables.computer.id === computer.id
            }
            onRequestAction={setPendingAction}
            onCreate={() => setCreateProvider(provider)}
            onDelete={setDeleteComputer}
            onResize={setResizeComputer}
          />
        ))
      )}

      <CreateCloudComputerDialog
        provider={createProvider}
        onOpenChange={(open) => {
          if (!open) setCreateProvider(null);
        }}
      />
      <ResizeCloudComputerDialog
        computer={resizeComputer}
        onOpenChange={(open) => {
          if (!open) setResizeComputer(null);
        }}
      />
      <DeleteCloudComputerDialog
        computer={deleteComputer}
        onOpenChange={(open) => {
          if (!open) setDeleteComputer(null);
        }}
      />

      <AlertDialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmation?.label} {pendingAction?.computer.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmation?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={computerAction.isPending}
              onClick={(event) => {
                event.preventDefault();
                confirmPendingAction();
              }}
            >
              {confirmation?.label}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ProviderSection({
  provider,
  isBusy,
  onRequestAction,
  onCreate,
  onDelete,
  onResize,
}: {
  provider: CloudComputerProvider;
  isBusy: (computer: CloudComputer) => boolean;
  onRequestAction: (action: PendingAction) => void;
  onCreate: () => void;
  onDelete: (computer: CloudComputer) => void;
  onResize: (computer: CloudComputer) => void;
}) {
  const running = provider.computers.filter(
    (computer) => computer.state === "running",
  ).length;
  const [listRef, listWidth] = useElementWidth<HTMLElement>();
  const columns = columnsForWidth(listWidth);
  return (
    <section
      ref={listRef}
      className="space-y-2"
      data-testid={`cloud-computers-${provider.id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">{provider.label}</h2>
        <div className="flex items-center gap-3">
          {provider.computers.length > 0 && (
            <span className="hidden text-sm text-muted-foreground sm:inline">
              {running} of {provider.computers.length} running
            </span>
          )}
          {provider.configured && !provider.error && (
            <Button size="sm" onClick={onCreate}>
              <Plus className="mr-2 h-4 w-4" />
              New computer
            </Button>
          )}
        </div>
      </div>
      {!provider.configured ? (
        <EmptyMessage message={`${provider.label} is not configured`} />
      ) : provider.error ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{provider.label} unavailable</AlertTitle>
          <AlertDescription>{provider.error}</AlertDescription>
        </Alert>
      ) : provider.computers.length === 0 ? (
        <EmptyMessage message="No computers found" />
      ) : (
        <div className="-mx-3 divide-y border-y md:mx-0 md:rounded-md md:border">
          {provider.computers.map((computer) => (
            <ComputerRow
              key={computer.id}
              computer={computer}
              columns={columns}
              isBusy={isBusy(computer)}
              onAction={(action) => onRequestAction({ computer, action })}
              onDelete={() => onDelete(computer)}
              onResize={() => onResize(computer)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ComputerRow({
  computer,
  columns,
  isBusy,
  onAction,
  onDelete,
  onResize,
}: {
  computer: CloudComputer;
  columns: RowColumns;
  isBusy: boolean;
  onAction: (action: CloudComputerAction) => void;
  onDelete: () => void;
  onResize: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const canRestart = !isBusy && computer.state === "running";
  return (
    <div data-testid="cloud-computer">
      <div className="flex items-center gap-3 px-3 py-1.5">
        {/* In a narrow list the row only shows name and power button; tapping the name expands the rest. */}
        <button
          type="button"
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2 text-left",
            columns.inline && "pointer-events-none cursor-default",
          )}
          aria-expanded={columns.inline ? undefined : expanded}
          tabIndex={columns.inline ? -1 : undefined}
          onClick={() => setExpanded((value) => !value)}
        >
          <StateDot computer={computer} />
          <span className="truncate font-medium" title={computer.name}>
            {computer.name}
          </span>
          {!columns.inline && (
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                expanded && "rotate-180",
              )}
            />
          )}
        </button>
        {columns.size && (
          <ComputerDetails
            computer={computer}
            sizeOnly={!columns.details}
            className={cn(
              "shrink-0 truncate",
              columns.details ? "w-56" : "w-40",
            )}
            onResize={onResize}
          />
        )}
        {columns.inline && (
          <div className="w-36 shrink-0">
            {computer.publicIps[0] && <CopyableIp ip={computer.publicIps[0]} />}
          </div>
        )}
        {columns.privateIp && (
          <div className="w-32 shrink-0">
            {computer.privateIps[0] && (
              <CopyableIp ip={computer.privateIps[0]} isPrivate />
            )}
          </div>
        )}
        {columns.inline && (
          <div className="flex shrink-0 items-center">
            <IconAction
              label="Restart"
              icon={RotateCw}
              disabled={!canRestart}
              onClick={() => onAction("restart")}
            />
            <IconAction
              label="Delete"
              ariaLabel={`Delete ${computer.name}`}
              icon={Trash2}
              destructive
              disabled={isBusy}
              onClick={onDelete}
            />
          </div>
        )}
        <PowerButton computer={computer} isBusy={isBusy} onAction={onAction} />
      </div>
      {expanded && !columns.inline && (
        <div className="space-y-2 px-3 pb-2 pl-7">
          <ComputerDetails computer={computer} onResize={onResize} />
          {(computer.publicIps.length > 0 ||
            computer.privateIps.length > 0) && (
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {computer.publicIps.map((ip) => (
                <CopyableIp key={ip} ip={ip} />
              ))}
              {computer.privateIps.map((ip) => (
                <CopyableIp key={ip} ip={ip} isPrivate />
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!canRestart}
              onClick={() => onAction("restart")}
            >
              <RotateCw className="mr-2 h-4 w-4" />
              Restart
            </Button>
            <Button
              variant="outline"
              size="sm"
              aria-label={`Delete ${computer.name}`}
              disabled={isBusy}
              onClick={onDelete}
            >
              <Trash2 className="mr-2 h-4 w-4 text-destructive" />
              Delete
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const STATE_LABELS: Record<CloudComputerState, string> = {
  running: "Running",
  stopped: "Stopped",
  starting: "Starting",
  stopping: "Stopping",
  restarting: "Restarting",
  resizing: "Resizing",
  error: "Error",
  other: "Other",
};

const DOT_STYLES: Record<CloudComputerState, string> = {
  running: "bg-green-500",
  stopped: "bg-muted-foreground/40",
  starting: "bg-amber-500 animate-pulse",
  stopping: "bg-amber-500 animate-pulse",
  restarting: "bg-amber-500 animate-pulse",
  resizing: "bg-amber-500 animate-pulse",
  error: "bg-destructive",
  other: "bg-muted-foreground/40",
};

function stateLabel(computer: CloudComputer) {
  return computer.state === "other"
    ? computer.providerState
    : STATE_LABELS[computer.state];
}

/** The computer's state, shown as a dot before its name. */
function StateDot({ computer }: { computer: CloudComputer }) {
  return (
    <span
      className={cn(
        "h-2 w-2 shrink-0 rounded-full",
        DOT_STYLES[computer.state],
      )}
      title={`${stateLabel(computer)} (${computer.providerState})`}
      aria-label={stateLabel(computer)}
      role="img"
    />
  );
}

/** Start for stopped computers, Stop for running ones; disabled while a transition runs. */
function PowerButton({
  computer,
  isBusy,
  onAction,
}: {
  computer: CloudComputer;
  isBusy: boolean;
  onAction: (action: CloudComputerAction) => void;
}) {
  const action: CloudComputerAction | null =
    computer.state === "running"
      ? "stop"
      : computer.state === "stopped"
        ? "start"
        : null;
  if (!action) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="h-8 w-28 shrink-0"
        disabled
        title={computer.providerState}
      >
        {computer.state === "error" ? (
          <AlertCircle className="mr-1.5 h-3.5 w-3.5" />
        ) : (
          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
        )}
        <span className="truncate">{stateLabel(computer)}</span>
      </Button>
    );
  }
  const isStart = action === "start";
  const Icon = isStart ? Play : Square;
  return (
    <Button
      variant={isStart ? "default" : "outline"}
      size="sm"
      className={cn(
        "h-8 w-28 shrink-0",
        !isStart &&
          "border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive",
      )}
      aria-label={`${ACTION_CONFIRMATIONS[action].label} ${computer.name}`}
      disabled={isBusy}
      onClick={() => onAction(action)}
    >
      <Icon className="mr-1.5 h-3.5 w-3.5 fill-current" />
      {ACTION_CONFIRMATIONS[action].label}
    </Button>
  );
}

function IconAction({
  label,
  ariaLabel,
  icon: Icon,
  destructive,
  disabled,
  onClick,
}: {
  label: string;
  ariaLabel?: string;
  icon: typeof RotateCw;
  destructive?: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          aria-label={ariaLabel ?? label}
          disabled={disabled}
          onClick={onClick}
        >
          <Icon className={cn("h-4 w-4", destructive && "text-destructive")} />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/** "size · region · os"; the size opens the resize dialog. */
function ComputerDetails({
  computer,
  sizeOnly,
  className,
  onResize,
}: {
  computer: CloudComputer;
  sizeOnly?: boolean;
  className?: string;
  onResize: () => void;
}) {
  const rest = sizeOnly
    ? ""
    : [computer.region, computer.os].filter(Boolean).join(" · ");
  return (
    <span
      className={cn("text-xs text-muted-foreground", className)}
      title={[computer.size, rest].filter(Boolean).join(" · ")}
    >
      {computer.size && (
        <button
          type="button"
          className="underline decoration-dotted underline-offset-2 hover:text-foreground"
          aria-label={`Resize ${computer.name} (${computer.size})`}
          onClick={onResize}
        >
          {computer.size}
        </button>
      )}
      {computer.size && rest && " · "}
      {rest}
    </span>
  );
}

function CopyableIp({ ip, isPrivate }: { ip: string; isPrivate?: boolean }) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex items-center gap-1 font-mono text-sm hover:underline",
        isPrivate && "text-muted-foreground",
      )}
      title={isPrivate ? "Private IP" : "Public IP"}
      onClick={() => {
        void navigator.clipboard.writeText(ip);
        toast.success("IP copied");
      }}
    >
      {ip}
      <Copy className="h-3 w-3" />
    </button>
  );
}

function EmptyMessage({ message }: { message: string }) {
  return (
    <div className="flex min-h-16 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
      {message}
    </div>
  );
}
